import { useEffect, useMemo, useRef, useState } from 'react';
import {
  apiClient,
  type EventSummary,
  type EventStatus,
  type EventSeverity,
  ApiError,
} from '@/lib/api/client';
import { cn } from '@/lib/utils';
import { useHotkeys } from '@/lib/useHotkeys';
import { useDialogFocus } from '@/lib/useDialogFocus';
import { SnoozePopover } from './SnoozePopover';
import { UndoToast } from './UndoToast';
import { snoozePresets } from './snoozeTimes';
import { ConvertToTaskModal } from './ConvertToTaskModal';

interface Props {
  onBack: () => void;
}

/** Status vocabulary matches the brand: Seen / Later / Done. `new` is the
 *  unseen-arrival state — surfaced as "Inbox" so users don't see a 4th-status
 *  mismatch ("New" alongside Seen/Later/Done reads as four parallel labels). */
const STATUS_LABELS: Record<EventStatus, string> = {
  new: 'Inbox',
  seen: 'Seen',
  later: 'Later',
  done: 'Done',
};

const SEVERITY_COLOR: Record<EventSeverity, string> = {
  info: 'oklch(0.72 0.14 235)',
  warn: 'oklch(0.78 0.17 65)',
  urgent: 'oklch(0.62 0.22 25)',
};

type Filter = EventStatus | 'all';
type ConnectionStatus = 'live' | 'stale' | 'offline';

type Undo = {
  message: string;
  revert: () => Promise<void> | void;
};

export function InboxView({ onBack }: Props) {
  const [filter, setFilter] = useState<Filter>('new');
  const [events, setEvents] = useState<EventSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [snoozeAnchor, setSnoozeAnchor] = useState<HTMLElement | null>(null);
  const [conn, setConn] = useState<ConnectionStatus>('live');
  const [undo, setUndo] = useState<Undo | null>(null);
  // Monotonic counter so UndoToast gets a NEW key only when a new undo is set,
  // not on every parent re-render. Date.now() restarted the 5s timer every render.
  const undoSeq = useRef(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const failuresRef = useRef(0);
  const listRef = useRef<HTMLUListElement | null>(null);

  async function refresh(silent = false) {
    try {
      const r = await apiClient.listEvents(filter === 'all' ? {} : { status: filter });
      setEvents(r.events);
      failuresRef.current = 0;
      setConn('live');
      if (!silent) {
        setErr(null);
        if (selectedId && !r.events.find((e) => e.event_id === selectedId)) {
          setSelectedId(null);
        }
      }
    } catch (e) {
      failuresRef.current += 1;
      if (failuresRef.current >= 3) setConn('offline');
      else setConn('stale');
      if (!silent) {
        setErr(e instanceof ApiError ? e.message : 'Failed to load');
      }
    }
  }

  useEffect(() => {
    refresh();
    // Poll every 10s — BUT skip when tab is hidden. Previously the interval
    // hammered the API forever in background tabs, accumulating failures
    // before the user returned (which then showed "Offline" on focus-in).
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') refresh(true);
    }, 10_000);
    // Refresh immediately on tab return so the user sees fresh data.
    function onVis() {
      if (document.visibilityState === 'visible') refresh(true);
    }
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const selectedIndex = useMemo(() => {
    if (!events || !selectedId) return -1;
    return events.findIndex((e) => e.event_id === selectedId);
  }, [events, selectedId]);

  function move(dir: -1 | 1) {
    if (!events || events.length === 0) return;
    const idx = selectedIndex < 0 ? (dir === 1 ? 0 : events.length - 1) : selectedIndex + dir;
    const clamped = Math.max(0, Math.min(events.length - 1, idx));
    const next = events[clamped];
    if (next) {
      setSelectedId(next.event_id);
      // Scroll into view if needed
      const el = listRef.current?.querySelector(`[data-event-id="${next.event_id}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    }
  }

  async function applyStatus(event_id: string, next: EventStatus, label: string) {
    const event = events?.find((e) => e.event_id === event_id);
    if (!event) return;
    const prev = event.status;
    const prevSnoozed = event.snoozed_until;
    // Optimistic remove from current list if filter wouldn't match the new status
    setEvents((rows) => {
      if (!rows) return rows;
      if (filter === 'all') {
        return rows.map((r) =>
          r.event_id === event_id ? { ...r, status: next, snoozed_until: null } : r,
        );
      }
      if (next === filter) {
        return rows.map((r) =>
          r.event_id === event_id ? { ...r, status: next, snoozed_until: null } : r,
        );
      }
      return rows.filter((r) => r.event_id !== event_id);
    });
    try {
      await apiClient.updateEvent(event_id, { status: next, snoozed_until: null });
    } catch (e) {
      // Optimistic rollback — surface a destructive UndoToast variant so the
      // user sees the row reappear, not just an error banner buried up top.
      refresh();
      undoSeq.current += 1;
      setUndo({
        message: `Couldn't mark ${label}, restored. ${e instanceof ApiError ? e.message : 'Try again?'}`,
        revert: async () => {
          setUndo(null);
        },
      });
      return;
    }
    undoSeq.current += 1;
    setUndo({
      message: `Marked ${label}.`,
      revert: async () => {
        await apiClient.updateEvent(event_id, {
          status: prev,
          snoozed_until: prevSnoozed ? new Date(prevSnoozed).toISOString() : null,
        });
        await refresh();
        setUndo(null);
      },
    });
  }

  async function snooze(event_id: string, untilIso: string) {
    const event = events?.find((e) => e.event_id === event_id);
    if (!event) return;
    const prev = event.status;
    const prevSnoozed = event.snoozed_until;
    setSnoozeOpen(false);
    // Optimistic: remove from "new"; in "later" filter it'd appear if still snoozed-in-future
    setEvents((rows) => (rows ? rows.filter((r) => r.event_id !== event_id) : rows));
    try {
      await apiClient.updateEvent(event_id, { status: 'later', snoozed_until: untilIso });
    } catch (e) {
      // Optimistic rollback toast (matches applyStatus failure path).
      refresh();
      undoSeq.current += 1;
      setUndo({
        message: `Couldn't snooze, restored. ${e instanceof ApiError ? e.message : 'Try again?'}`,
        revert: async () => {
          setUndo(null);
        },
      });
      return;
    }
    undoSeq.current += 1;
    setUndo({
      message: `Snoozed until ${new Date(untilIso).toLocaleString()}.`,
      revert: async () => {
        await apiClient.updateEvent(event_id, {
          status: prev,
          snoozed_until: prevSnoozed ? new Date(prevSnoozed).toISOString() : null,
        });
        await refresh();
        setUndo(null);
      },
    });
  }

  const selected = events?.find((e) => e.event_id === selectedId) ?? null;

  useHotkeys(
    {
      j: () => move(1),
      ArrowDown: () => move(1),
      k: () => move(-1),
      ArrowUp: () => move(-1),
      e: () => selected && applyStatus(selected.event_id, 'done', 'done'),
      d: () => selected && applyStatus(selected.event_id, 'done', 'done'),
      l: () => selected && applyStatus(selected.event_id, 'later', 'later'),
      s: (e) => {
        if (!selected) return;
        const target = (e.target as HTMLElement) ?? null;
        setSnoozeAnchor(target);
        setSnoozeOpen(true);
      },
      '1': () => snoozeShortcut(0),
      '2': () => snoozeShortcut(1),
      '3': () => snoozeShortcut(2),
      '4': () => snoozeShortcut(3),
      '5': () => snoozeShortcut(4),
      Escape: () => {
        if (snoozeOpen) setSnoozeOpen(false);
        else if (helpOpen) setHelpOpen(false);
        else setSelectedId(null);
      },
      '?': () => setHelpOpen((v) => !v),
    },
    { enabled: !undo || true },
  );

  function snoozeShortcut(i: number) {
    if (!snoozeOpen || !selected) return;
    const presets = snoozePresets();
    const p = presets[i];
    if (p) snooze(selected.event_id, p.iso);
  }

  return (
    <main className="min-h-dvh motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-150 motion-safe:ease-out bg-background text-foreground antialiased">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-card focus:px-4 focus:py-2 focus:text-sm">Skip to content</a>
      <header className="border-b border-border bg-card/50">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-y-2 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onBack}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors duration-150 hover:border-ring/60 hover:text-foreground"
            >
              ← Workspace
            </button>
            <h1 className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Inbox
            </h1>
            <ConnectionDot status={conn} />
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {(['new', 'seen', 'later', 'done', 'all'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={cn(
                  'rounded-md px-2.5 py-1.5 text-xs font-medium capitalize transition-colors sm:px-3',
                  filter === s
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {s}
              </button>
            ))}
            <button
              onClick={() => setHelpOpen((v) => !v)}
              className="ml-2 rounded-md border border-border px-2 py-1.5 font-mono text-[10px] text-muted-foreground hover:border-ring/60 hover:text-foreground"
              aria-label="Keyboard shortcuts"
            >
              ?
            </button>
          </div>
        </div>
      </header>

      {/* Three-pane layout. On mobile (<md): selecting an event swaps the
          view to the detail pane (full-screen), with a Back-to-list button.
          On desktop (≥md): two-pane side-by-side, list always visible. */}
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-0 px-0 md:grid-cols-[1fr_440px]">
        <div
          className={cn(
            'border-r border-border',
            // Hide list when a row is selected on mobile (detail pane takes over).
            selectedId && 'hidden md:block',
          )}
        >
          {err && (
            <div role="alert" className="m-6 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
              {err}
            </div>
          )}
          {events === null ? (
            <InboxSkeleton />
          ) : events.length === 0 ? (
            <Empty filter={filter} onSwitchFilter={setFilter} />
          ) : (
            <ul role="list" ref={listRef} className="divide-y divide-border">
              {events.map((e) => (
                <li key={e.event_id}>
                  <button
                    data-event-id={e.event_id}
                    onClick={() => setSelectedId(e.event_id)}
                    onDoubleClick={() => applyStatus(e.event_id, 'done', 'done')}
                    className={cn(
                      'group w-full border-l-[3px] py-3 pl-4 pr-6 text-left transition-colors duration-100 hover:bg-card/40',
                      selectedId === e.event_id && 'bg-card/40',
                    )}
                    style={{ borderLeftColor: SEVERITY_COLOR[e.severity] }}
                  >
                    <span className="sr-only">Severity {e.severity}. </span>
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          {/* Source identity: leading dot in source color +
                              muted-mono name. Was tinted-pill (color30 bg +
                              full-color text) — per-row colored chips made
                              the list a confetti of color, drowning out the
                              3px severity stripe (DESIGN.md §1: severity
                              ONLY on left border, never chip/dot/fill). The
                              ~6px dot keeps source identity scannable
                              without competing for color budget. */}
                          <span
                            aria-hidden
                            className="size-1.5 shrink-0 rounded-full"
                            style={{ background: e.source_color ?? 'var(--primary)' }}
                          />
                          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            {e.source_name}
                          </span>
                          <span className="font-mono text-[10px] text-muted-foreground">
                            · {e.type}
                          </span>
                          {e.status !== 'new' && (
                            <span className="font-mono text-[10px] text-muted-foreground">
                              · {STATUS_LABELS[e.status]}
                              {e.status === 'later' && e.snoozed_until && (
                                <>
                                  {' '}
                                  · wakes {timeShort(e.snoozed_until)}
                                </>
                              )}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 truncate text-sm text-foreground">{e.title}</div>
                      </div>
                      <div className="font-mono text-[10px] text-muted-foreground">
                        {timeAgo(e.created_at)}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className={cn(!selectedId && 'hidden md:block')}>
          {/* Mobile back-to-list button (hidden on desktop where list is
              persistent in the left pane). */}
          {selectedId && (
            <button
              type="button"
              onClick={() => setSelectedId(null)}
              className="m-3 inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground hover:border-ring/60 hover:text-foreground md:hidden"
            >
              ← Back to list
            </button>
          )}
          <DetailPane
            event={selected}
            onStatusChange={(id, status, label) => applyStatus(id, status, label)}
            onSnoozeClick={(el) => {
              setSnoozeAnchor(el);
              setSnoozeOpen(true);
            }}
            onConvert={() => setConvertOpen(true)}
          />
        </div>
      </div>

      {snoozeOpen && selected && (
        <SnoozePopover
          anchorEl={snoozeAnchor}
          onSnooze={(iso) => snooze(selected.event_id, iso)}
          onClose={() => setSnoozeOpen(false)}
        />
      )}
      {undo && (
        <UndoToast
          key={undoSeq.current}
          message={undo.message}
          onUndo={async () => {
            await undo.revert();
          }}
          onExpire={() => setUndo(null)}
        />
      )}
      {helpOpen && <HelpOverlay onClose={() => setHelpOpen(false)} />}
      {convertOpen && selected && (
        <ConvertToTaskModal
          event={selected}
          onConverted={({ task_id }) => {
            setConvertOpen(false);
            // Optimistic: stamp task_id on the row so the button flips to
            // "Linked task ✓" immediately. refresh pulls server truth.
            setEvents((rows) =>
              rows ? rows.map((r) => (r.event_id === selected.event_id ? { ...r, task_id } : r)) : rows,
            );
            refresh(true);
          }}
          onClose={() => setConvertOpen(false)}
        />
      )}
    </main>
  );
}

function InboxSkeleton() {
  return (
    <ul role="list" className="divide-y divide-border" aria-busy="true" aria-label="Loading inbox">
      {Array.from({ length: 6 }).map((_, i) => (
        <li key={i} className="px-6 py-3.5">
          <div className="h-3 w-1/3 animate-pulse rounded bg-card" />
          <div className="mt-2 h-4 w-2/3 animate-pulse rounded bg-card" />
        </li>
      ))}
    </ul>
  );
}

function Empty({ filter, onSwitchFilter }: { filter: Filter; onSwitchFilter: (f: Filter) => void }) {
  if (filter === 'new') {
    return (
      <div className="m-6 space-y-4 rounded-xl border border-dashed border-border bg-card/40 px-5 py-12 text-center">
        <div className="mx-auto inline-grid size-12 place-items-center rounded-full bg-secondary/60">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-secondary-foreground" aria-hidden>
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        </div>
        <div className="space-y-1">
          <div className="text-base font-semibold text-foreground">Inbox zero</div>
          <div className="text-sm text-muted-foreground">
            You're caught up. Events from your sources will appear here.
          </div>
        </div>
        <div className="mx-auto max-w-sm space-y-2 rounded-lg border border-border bg-background/40 p-3 text-left">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Send a test event
          </div>
          <pre className="overflow-x-auto whitespace-pre-wrap break-all font-mono text-[10px] text-foreground">
{`curl -X POST https://api.apetech.studio/v1/events \\
  -H 'Authorization: Bearer sk_live_...' \\
  -H 'Content-Type: application/json' \\
  -d '{"external_id":"hi","type":"feedback","title":"Hello PM","severity":"urgent"}'`}
          </pre>
        </div>
      </div>
    );
  }
  return (
    <div className="m-6 rounded-xl border border-dashed border-border bg-card/40 px-5 py-12 text-center">
      <div className="text-base text-foreground">No {filter} events</div>
      <div className="mt-1 text-sm text-muted-foreground">
        Switch to{' '}
        <button
          type="button"
          onClick={() => onSwitchFilter('new')}
          className="rounded border border-border/60 bg-card px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-foreground hover:border-ring/60"
        >
          Inbox
        </button>{' '}
        to see what's open.
      </div>
    </div>
  );
}

function DetailPane({
  event,
  onStatusChange,
  onSnoozeClick,
  onConvert,
}: {
  event: EventSummary | null;
  onStatusChange: (id: string, status: EventStatus, label: string) => void;
  onSnoozeClick: (el: HTMLElement) => void;
  onConvert: () => void;
}) {
  if (!event) {
    return (
      <div className="grid place-items-center p-12 text-center">
        <div className="space-y-2">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
            Select an event
          </div>
          <div className="text-xs text-muted-foreground/70">
            <kbd className="rounded border border-border bg-card px-1 py-0.5 font-mono text-[10px]">
              j
            </kbd>
            /
            <kbd className="rounded border border-border bg-card px-1 py-0.5 font-mono text-[10px]">
              k
            </kbd>{' '}
            to navigate ·{' '}
            <kbd className="rounded border border-border bg-card px-1 py-0.5 font-mono text-[10px]">
              ?
            </kbd>{' '}
            for shortcuts
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-4 p-6">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {event.source_name} · {event.type}
        </div>
        <h2 className="mt-2 text-xl font-semibold tracking-tight">{event.title}</h2>
        <div className="mt-2 flex items-center gap-2">
          {/* Severity chip: tinted bg + leading dot in severity color + text
              in foreground to pass WCAG 1.4.11 at 10px. Severity colors are
              calibrated for the 3px left-border use (decorative), not for
              small text contrast. */}
          <span
            className="inline-flex items-center gap-1.5 rounded-sm bg-foreground/5 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-foreground"
            aria-label={`Severity: ${event.severity}`}
          >
            <span
              className="size-1.5 rounded-full"
              aria-hidden
              style={{ background: SEVERITY_COLOR[event.severity] }}
            />
            {event.severity}
          </span>
          <span className="font-mono text-[10px] text-muted-foreground">
            {STATUS_LABELS[event.status]} · {new Date(event.created_at).toLocaleString()}
          </span>
        </div>
      </div>
      {event.body_md && (
        <div className="rounded-md border border-border bg-card p-3 text-sm text-foreground whitespace-pre-wrap">
          {event.body_md}
        </div>
      )}
      {event.url && (
        <a
          href={event.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-xs text-primary hover:underline"
        >
          Open in source ↗
        </a>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <ActionButton
          label="Done"
          shortcut="e"
          active={event.status === 'done'}
          onClick={() => onStatusChange(event.event_id, 'done', 'done')}
        />
        <ActionButton
          label="Snooze"
          shortcut="s"
          active={event.status === 'later'}
          onClick={(e) => onSnoozeClick(e.currentTarget)}
        />
        <ActionButton
          label="Seen"
          active={event.status === 'seen'}
          onClick={() => onStatusChange(event.event_id, 'seen', 'seen')}
        />
        <ActionButton
          label="New"
          active={event.status === 'new'}
          onClick={() => onStatusChange(event.event_id, 'new', 'new')}
        />
        {/* Convert to task — primary affordance for routing an event into
            a project. Shows "Open task →" instead when already converted
            (server returns task_id on 409 — we surface that path here too). */}
        {event.task_id ? (
          <span className="ml-2 inline-flex items-center gap-1 rounded-md border border-border bg-card/60 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Linked task ✓
          </span>
        ) : (
          <button
            type="button"
            onClick={onConvert}
            className="ml-2 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-primary-foreground transition-opacity hover:opacity-90"
          >
            → Convert to task
          </button>
        )}
      </div>
      <div className="rounded-md border border-border bg-background p-3 font-mono text-[10px] text-muted-foreground">
        <div>event_id: {event.event_id}</div>
        <div>external_id: {event.external_id}</div>
        {event.project_id && <div>routed → {event.project_id}</div>}
      </div>
    </div>
  );
}

function ActionButton({
  label,
  shortcut,
  active,
  onClick,
}: {
  label: string;
  shortcut?: string;
  active: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border text-muted-foreground hover:border-ring/60 hover:text-foreground',
      )}
    >
      {label}
      {shortcut && (
        <kbd
          className={cn(
            'rounded px-1 py-0.5 font-mono text-[9px]',
            active ? 'bg-primary-foreground/20 text-primary-foreground/80' : 'border border-border text-muted-foreground/70',
          )}
        >
          {shortcut}
        </kbd>
      )}
    </button>
  );
}

function ConnectionDot({ status }: { status: ConnectionStatus }) {
  const color =
    status === 'live'
      ? 'oklch(0.72 0.18 148)' // green
      : status === 'stale'
        ? 'oklch(0.78 0.17 65)' // amber
        : 'oklch(0.62 0.22 25)'; // red
  const label =
    status === 'live'
      ? 'Connected (polling)'
      : status === 'stale'
        ? 'Reconnecting…'
        : 'Offline';
  // role=status was re-announcing on every re-render even when status hadn't
  // changed. Track previous status and only mount the SR-only announcement
  // when it flips, so VoiceOver/NVDA don't chatter on every poll.
  const prevRef = useRef(status);
  const changed = prevRef.current !== status;
  useEffect(() => {
    prevRef.current = status;
  }, [status]);
  return (
    <span className="ml-2 inline-flex items-center gap-1.5" title={label}>
      <span className="block size-1.5 rounded-full" style={{ background: color }} aria-hidden />
      <span className="font-mono text-[10px] text-muted-foreground">{label}</span>
      {changed && (
        <span className="sr-only" role="status" aria-live="polite">
          Connection {label}
        </span>
      )}
    </span>
  );
}

function HelpOverlay({ onClose }: { onClose: () => void }) {
  const panelRef = useDialogFocus<HTMLDivElement>();
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-overlay-title"
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 px-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md space-y-4 rounded-2xl border border-border bg-card p-7 shadow-2xl"
      >
        <div className="space-y-1">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Shortcuts
          </div>
          <h2 id="help-overlay-title" className="text-lg font-semibold">Keyboard</h2>
        </div>
        <div className="space-y-1.5">
          <Row k="j / ↓" label="Next event" />
          <Row k="k / ↑" label="Previous event" />
          <Row k="e" label="Mark Done" />
          <Row k="l" label="Move to Later" />
          <Row k="s" label="Open Snooze (then 1-5 for presets)" />
          <Row k="Esc" label="Clear selection / close popover" />
          <Row k="?" label="This panel" />
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-2 w-full rounded-md border border-border bg-background px-4 py-2 text-xs font-medium text-muted-foreground hover:border-ring/60 hover:text-foreground"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

function Row({ k, label }: { k: string; label: string }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md px-1 py-1">
      <span className="text-sm text-foreground">{label}</span>
      <kbd className="rounded border border-border bg-background px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
        {k}
      </kbd>
    </div>
  );
}

function timeAgo(ms: number): string {
  const diff = Date.now() - ms;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d`;
  return new Date(ms).toLocaleDateString();
}

function timeShort(ms: number): string {
  const d = new Date(ms);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  const diffDays = Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays >= 1 && diffDays < 7) {
    return d.toLocaleDateString([], { weekday: 'short' }) + ' ' +
      d.toLocaleTimeString([], { hour: 'numeric' });
  }
  return d.toLocaleDateString();
}
